import { sequence } from '@angular/animations';
import { DigitalSigningModel } from './digitalSigning.model';
import { VehicleSummaryModel } from './vehicle.model';
import { VehicleSearchResultModel } from './vehicleSearchResult.model';
import { OpportunityResultModel } from './opportunityResult.model';
import { ContactModel, ContactResultModel } from "./contact.model";
import { LookupInternalModel, LookupExternalModel, LookupModel } from "app/models/shared/lookup.model";
import { TaxStatusModel } from "./taxStatus.model";
import { SaleBalanceType, ProductClass, TaxStatusEnum, ProductSubClass, TimePeriod, Salutation } from 'app/utility/enums';
import { CustomFieldResultModel } from '../shared/customField.model';
import { PartResultModel } from './partResult.model';



export interface SaleDetailsModel
{
	saleId: string;
	saleNo: number;
	recordType: string;
	saleTypeEnum: string;
	saleStatusEnum: string;
	opportunityId: string;
	createdDate: string;
	modifiedDate: string;
	isSaleReadOnly: boolean;
	saleFinance: SaleDetails_SaleFinance;
	summary: SaleDetails_Summary;
	general: SaleDetails_General;
	vehicles: SaleDetails_Vehicles;
	extras: SaleDetails_Extras;
	payments: SaleDetails_Payments;
	writeback: SaleDetails_Writeback;
	notes: SaleDetails_Notes;
	customFields: SaleDetails_CustomFields;
	status: SaleDetails_Status;
	workflow: SaleDetails_Workflow;
	delivery: SaleDetails_Delivery;
	insurance: SaleDetails_Insurance;
	settings: SaleDetails_Settings;
}

export interface SaleDetails_NestedDtoInterface
{
	saleId: string;
	saleNo: number;
}

export class SaleDetails_CustomFields implements SaleDetails_NestedDtoInterface
{
	saleId: string;
	saleNo: number;
	customField1: string;
	customField2: string;
	customField3: string;
	customField4: string;
	customField5: string;
	customField6: string;
	customField7: string;
	customField8: string;
	customField9: string;
	customField10: string;
	customField11: string;
	customField12: string;
	
	customField1_Object: CustomFieldResultModel;
	customField2_Object: CustomFieldResultModel;
	customField3_Object: CustomFieldResultModel;
	customField4_Object: CustomFieldResultModel;
	customField5_Object: CustomFieldResultModel;
	customField6_Object: CustomFieldResultModel;
	customField7_Object: CustomFieldResultModel;
	customField8_Object: CustomFieldResultModel;
	customField9_Object: CustomFieldResultModel;
	customField10_Object: CustomFieldResultModel;
	customField11_Object: CustomFieldResultModel;
	customField12_Object: CustomFieldResultModel;
}

class SaleDetails_Delivery implements SaleDetails_NestedDtoInterface
{
	saleId: string;
	saleNo: number;
	deliveryStatusEnum: number;
	deliveryStartById: string;
	deliveryStartDate: Date;
	deliveryEndById: string;
	deliveryEndDate: Date;
	deliveryInstructions: string;
	deliveryItemIds: string[];
	deliveryStatus: LookupInternalModel;
	deliveryItems: LookupExternalModel[];
	deliveryStartBy: ContactModel;
	deliveryEndBy: ContactResultModel;
}


class SaleDetails_Extras implements SaleDetails_NestedDtoInterface
{
	saleId: string;
	saleNo: number;
	saleProducts: SaleDetails_SaleProduct[];
}

export class SaleDetails_SaleProduct
{
	caption: string;
	description: string;
	saleProductId: string;
	saleId: string;
	vehicleId: string;
	productId: string;
	productClass: string;
	productSubClass: ProductSubClass;
	insStartDate: Date;
	insContractRef: string;
	insPaymentType: string;
	finSecurityType: string;
	finInstallment: number;
	customFieldsJson: string;
	partId: string;
	partQty: number;
	supplierId: string;
	categoryId: string;
	purCurrencyId: string;
	purTaxStatusEnum: number;
	purTaxRate: number;
	purRate: number;
	purAmount: number;
	purTaxAmount: number;
	purOffsetRateMethod: string;
	purOffsetAmount: number;
	saleCurrencyId: string;
	saleTaxStatusEnum: number;
	saleTaxRate: number;
	saleRate: number;
	saleAmount: number;
	saleOffsetRateMethod: string;
	saleOffsetAmount: number;
	saleComCurrencyId: string;
	saleComTaxStatusEnum: number;
	saleComTaxRate: number;
	saleComRate: number;
	saleComAmount: number;
	saleComTaxAmount: number;
	productVariant: string;
	periodCount: number;
	periodEnum: string;
	excessAmount: number;
	benefitAmount: number;
	isJoint: boolean;
	workflowStatusEnum: string;
	comment: string;
	reference: string;
    prefSupplierId: string;
    benefitFrom: number;
    benefitTo: number;
    benefitStep: number;
    jointFactor: number;
    customField1: string;
    customField2: string;
    claimLimit: number;
    dealershipId: string;
    partCaption: string;
    prefSupplierCaption: string;
    categoryCaption: string;
    periodCaption: string;
    excessCaption: string;
    claimLimitCaption: string;
    fullCaption: string;
    purCurrencyDisplay: string;
    saleCurrencyDisplay: string;
    hasCalculatedSalePrice: boolean;
    hasBenefitOption: boolean;
    hasJointOption: boolean;
    isFinanceCentralProduct: boolean;
    supportsInsPolicyBatch: boolean;
    quoteReference: string;
    requiresUnderwriting: boolean;
    validationText: string;
    iconProductValidation: string;
    dealershipReference: string;
    printButton: boolean;
    saleProductIds: string[];
    productRefEnumIsNone: boolean;
    
    category: LookupExternalModel;
    supplier: ContactResultModel;
    purTaxStatus: TaxStatusModel;
    saleTaxStatus: TaxStatusModel;
    saleComTaxStatus: TaxStatusModel;
    part: PartResultModel;
    prefSupplier: ContactResultModel;
    dealership: ContactResultModel;
}

export class SaleDetails_SaleProductMvi
{
    excludeDriversUnder25: boolean;
    isImmobilizerConfirmed: boolean;
    allowNamedDriversOnly: boolean;
    hasNoClaimsBonusProtection: boolean;
    hasRoadsideAssistance: boolean;
    hasWindscreenCover: boolean;
    voluntaryExcessAmount: number;
    rentalCarExcessAmount: number;
    vehicleGaragingAreaId: string;
    vehicleParkingTypeId: string;
    vehicleAnnualDistanceDriven: number;
    vehicleIntendedUseId: string;
    generalQuestionAnswersCsv: string;
    paymentMethodId: string;
    paymentPeriodId: string;
    isImmobilizerRequired: boolean | null;
    paymentFrequency: TimePeriod;
    paymentDay: number;
    paymentDate: Date;
    directDebitBankAccName: string;
    directDebitBankAccNo: string;
    paymentCustomerFirstName: string;
    paymentCustomerLastName: string;
    paymentCustomerPostalAddressLine1: string;
    paymentCustomerPostalAddressLine2: string;
    paymentCustomerPostalAddressSuburb: string;
    paymentCustomerPostalAddressCityId: string;
    paymentCustomerPostalAddressPostCode: string;
    quoteSaleTaxStatusEnum: TaxStatusEnum;
    quoteSaleAmount: number;
    quoteSaleTaxAmount: number;
    
    paymentCustomerPostalAddressCity: LookupExternalModel;
    vehicleGaragingArea: LookupExternalModel[];
    vehicleParkingType: LookupExternalModel[];
    vehicleIntendedUse: LookupExternalModel[];
    paymentMethod: LookupExternalModel[];
    paymentPeriod: LookupInternalModel[];
    drivers: SaleDetails_SaleProductMviDriver[];
}

export class SaleDetails_SaleProductMviDriver
{
    mviDriverId: string;
    salutation: Salutation;
    firstName: string;
    lastName: string;
    birthDate: Date;
    genderId: string;
    licenceTypeId: string;
    licenceYearsId: string;
    licenceCountryId: string;
    incidentsIn5Years: string;
    incidentsIn12Months: string;
    percentageUse: number;
}

export class SaleDetails_General implements SaleDetails_NestedDtoInterface
{
    saleId: string;
	saleNo: number;
	saleDate: string;
	accDate: string;
	settleDate: string;
	reference: string;
	dealershipId: string;
	busManagerId: string;
	salesPersonId: string;
	referralMethodId: string;
	campaignId: string;
	dealership: ContactResultModel;
	salesPerson: ContactResultModel;
	busManager: ContactResultModel;
	referralMethod: LookupExternalModel;
	campaign: LookupExternalModel;
	customers: SaleDetails_Customer[];
}

export class SaleDetails_Customer extends ContactResultModel
{
	saleCustomerId: string;
	sequence: number;
	customerId: string;
	customer: ContactResultModel;
}

export class SaleDetails_Notes implements SaleDetails_NestedDtoInterface
{
	saleId: string;
	saleNo: number;
	specialConditions: string;
	internalNotes: string;
	uninvitedDirectSale: boolean;
	saleSpecialConditionsList: LookupExternalModel;
}

export class SaleDetails_Payments implements SaleDetails_NestedDtoInterface
{
	saleId: string;
	saleNo: number;
	salePayments: SaleDetails_Payment[];

	payment1Amount: number;
	payment2Amount: number;
	payment3Amount: number;
	payment4Amount: number;

	settleDate: Date;
	summaryFinanceAmount: number;

	costEstFee: number;
	costEstTaxStatusEnum: number;
	costEstTaxStatus: TaxStatusEnum;
    costEstTaxRate: number;
    costEstTaxAmount: number;

	costReferralFee: number;
	costReferralTaxStatusEnum: number;
	costReferralTaxStatus: TaxStatusEnum;
    costReferralTaxRate: number;
    costReferralTaxAmount: number;

	finCompanyId: string;
	finCompany: ContactResultModel;
	finTermCount: number;
	finTermPeriodId: string;
	finTermPeriod: LookupInternalModel;
	finTermRate: number;	
}

export class SaleDetails_Payment
{

	saleId: string;
	saleNo: number;
	salePaymentId: string;
	paymentDate: Date;
	paymentAmount: number;
	transMethodId: string;
	transCheckedById: string;
	transCheckedDesc: string;
	transMethod: LookupExternalModel;
	transCheckedBy: ContactResultModel;
	reference: string;
	comment: string;
}

export class SaleDetails_SaleFinance implements SaleDetails_NestedDtoInterface
{
	saleId: string;
	saleNo: number;
	saleFinanceId: string;
	finAmount: number;
	finCompanyId: string;
	finCompany: ContactResultModel;
	caption: string;
}


export class SaleDetails_Status implements SaleDetails_NestedDtoInterface
{
	saleId: string;
	saleNo: number;
	saleStatusEnum: number;
	saleTypeEnum: number;
	saleStatus: LookupInternalModel;
	saleType: LookupInternalModel;
}

export class SaleDetails_Summary implements SaleDetails_NestedDtoInterface
{
	saleId: string;
	saleNo: number;
	caption: string;
	recordTypeEnum: number;
	recordType: string;
	createdDate: string;
	modifiedDate: string;
	createdById: string;
	createdBy: ContactResultModel;
	modifiedById: string;
	modifiedBy: ContactResultModel;
	salePaymentsTotalAmount: number;
	summarySaleAmount: number;
	summarySaleTaxAmount: number;
	summaryPurchaseAmount: number;
	summaryPurchaseTaxAmount: number;
	summaryPurchaseFee1Amount: number;
	summaryPurchaseFee1TaxAmount: number;
	summaryProductAmount: number;
	summaryProductTaxAmount: number;
	summaryProductPurchaseAmount: number;
	summaryProductPurchaseTaxAmount: number;
	summaryProductProfitAmount: number;
	summaryPaymentInsuranceAmount: number;
	summaryPaymentInsuranceTaxAmount: number;
	summaryPaymentInsurancePurchaseAmount: number;
	summaryPaymentInsurancePurchaseTaxAmount: number;
	summaryPaymentInsuranceCommissionAmount: number;
	summaryPaymentInsuranceCommissionTaxAmount: number;
	summaryPaymentInsuranceProfitAmount: number;
	summaryEquityInsuranceAmount: number;
	summaryEquityInsuranceTaxAmount: number;
	summaryEquityInsurancePurchaseAmount: number;
	summaryEquityInsurancePurchaseTaxAmount: number;
	summaryEquityInsuranceCommissionAmount: number;
	summaryEquityInsuranceCommissionTaxAmount: number;
	summaryEquityInsuranceProfitAmount: number;
	summaryVehicleRegistrationAmount: number;
	summaryVehicleRegistrationTaxAmount: number;
	summaryVehicleRegistrationPurchaseAmount: number;
	summaryVehicleRegistrationPurchaseTaxAmount: number;
	summaryVehicleRegistrationProfitAmount: number;
	summaryVehicleInsuranceAmount: number;
	summaryVehicleInsuranceTaxAmount: number;
	summaryVehicleInsurancePurchaseAmount: number;
	summaryVehicleInsurancePurchaseTaxAmount: number;
	summaryVehicleInsuranceProfitAmount: number;
	summaryBreakdownInsuranceAmount: number;
	summaryBreakdownInsuranceTaxAmount: number;
	summaryBreakdownInsurancePurchaseAmount: number;
	summaryBreakdownInsurancePurchaseTaxAmount: number;
	summaryBreakdownInsuranceProfitAmount: number;
	summaryVehicleServicePlanAmount: number;
	summaryVehicleServicePlanTaxAmount: number;
	summaryVehicleServicePlanPurchaseAmount: number;
	summaryVehicleServicePlanPurchaseTaxAmount: number;
	summaryVehicleServicePlanProfitAmount: number;
	summaryPaymentDODAmount: number;
	summaryPaymentDODTaxAmount: number;
	summaryPaymentDOSAmount: number;
	summaryPaymentDOSTaxAmount: number;
	summaryPaymentOtherAmount: number;
	summaryPaymentOtherTaxAmount: number;
	summaryFinanceAmount: number;
	summarySaleVehiclesSoldAmount: number;
	summarySaleSubTotalAmount: number;
	summarySaleNetTradeInsAmount: number;
	summaryBalanceNetPrice: number;
	summaryBalanceCashInstallments: number;
	summaryBalanceUnallocated: number;
	summaryFinanceOutstandingAmount: number;
	summaryFinanceTotal: number;
	summaryFinanceInterest: number;
	summaryAdminFeeAmount: number;
	summaryFinanceCost: number;
	summaryRepaymentWeek: number;
	summaryRepaymentWeek48: number;
	summaryRepaymentFortnight: number;
	summaryRepaymentMonth: number;
	summaryProfitCostEstAmount: number;
	summaryProfitCostEstTaxAmount: number;
	summaryProfitCostSecRegAmount: number;
	summaryProfitCostSecRegTaxAmount: number;
	summaryProfitAdminAmount: number;
	summaryProfitAdminTaxAmount: number;
	summaryProfitFinTermAmount: number;
	summaryProfitFinTermTaxAmount: number;
	financeCentralSummary: SaleDetails_FinanceCentralSummary;
	financeSummary: SaleDetails_FinanceSummary;
}

export interface SaleDetails_FinanceSummary
{
	company: string;
	termCaption: string;
	termCount: number;
	termPeriod: string;
	termRate: number;
	total: number;
	interest: number;
	cost: number;
	repaymentMonthly: number;
	repaymentFortnightly: number;
	repaymentWeekly: number;
	repaymentWeek48: number;
}

export class SaleDetails_FinanceCentralSummary
{
	status: string;
	total: number;
	reference: string;
	repayments: string;
	link: string;
	canWithdraw: boolean;
}


export interface SaleDetails_Vehicles
{
	saleId: string;
	saleNo: number;
	vehiclesSold: SaleDetails_Vehicle[];
	vehiclesPurchased: SaleDetails_Vehicle[];
}

export class SaleDetails_Vehicle
{
	saleVehicleId: string;
	saleId: string;
	vehicleId: string;
	saleVehicleType: 'Sale' | 'Purchase';
	salePriceAmount: number;
	salePriceTaxStatusEnum: number;
	salePriceTaxRate: number;
	salePriceTaxAmount: number;
	fee1Amount: number;
	fee1TaxRate: number;
	fee1TaxStatusEnum: number;
	fee1TaxAmount: number;
	altModel: string;
	altVariant: string;
	disableAmpdCorrespond: boolean;

	salePriceTaxStatus: TaxStatusModel;
	fee1TaxStatus: TaxStatusModel;
	encFinancier: string;
	vehicle: VehicleSummaryModel;

	// Transient fields for display purposes only
	vehicleRegistration: SaleDetails_SaleProduct;
	tyreStewardshipFee: SaleDetails_SaleProduct;
	breakdownInsurance: SaleDetails_SaleProduct;
	vehicleInsurance: SaleDetails_SaleProduct;
}


export interface Workflow
{
	saleId: string;
	saleNo: number;
	workflowStatusEnum: number;
	financeWorkflowStatusEnum: number;
	externalSystemId: string;
	friendlyExternalSystemId: string;
	signingRequest: null;
	workflowStatus: LookupInternalModel;
	financeWorkflowStatus: LookupInternalModel;
	externalSystemStatus: LookupExternalModel;
}

export class SaleDetails_Writeback implements SaleDetails_NestedDtoInterface
{
	saleId: string;
	saleNo: number;
	summaryWritebackBalanceAmount: number;
	summarySaleWritebackAmount: number;
	summarySaleWritebackTaxAmount: number;
	summaryPurchaseWritebackAmount: number;
	summaryPurchaseWritebackTaxAmount: number;
	vehiclesSoldWriteback: SaleDetails_VehicleWriteback[];
	vehiclesPurchasedWriteback: SaleDetails_VehicleWriteback[];
}

export class SaleDetails_VehicleWriteback
{
	saleVehicleId: string;
	writebackBalanceAmount: number;
	writebackTaxStatusEnum: number;
	writebackTaxRate: number;
	writebackAmount: number;
	writebackTaxAmount: number;
	writebackTaxStatus: TaxStatusModel;
	vehicleId: string;
	vehicle: VehicleSummaryModel;
}

class SaleDetails_Settings implements SaleDetails_NestedDtoInterface
{
	saleId: string;
	saleNo: number;
}

class SaleDetails_Workflow implements SaleDetails_NestedDtoInterface
{
	saleId: string;
	saleNo: number;
	workflowStatusEnum: number;
	financeWorkflowStatusEnum: number;
	externalSystemId: string;
	friendlyExternalSystemId: string;
	signingRequest: DigitalSigningModel;

	workflowStatus: LookupInternalModel;
	financeWorkflowStatus: LookupInternalModel;
	externalSystemStatus: LookupExternalModel;
}

class SaleDetails_Insurance implements SaleDetails_NestedDtoInterface
{
	saleId: string;
	saleNo: number;

	insDealer: boolean;
	insCompanyId: string;
	insCompany: ContactResultModel;
	insBranch: string;
	insPolicyDesc: string;
	insCoverDesc: string;
	insExpiryDate: Date;
}

